import { useEffect, useState } from 'react';
import { MessageSquarePlus } from 'lucide-react';
import {
  Button,
  Modal,
  Select,
  Textarea,
  useToast,
  type SelectOption,
} from '../../ui';
import {
  blankTaskUpdateDraft,
  validateTaskUpdateDraft,
} from '../../features/sprintboard/models/sprintboardModel';
import type {
  TaskUpdate,
  TaskUpdateDraft,
  UpdateStatus,
} from '../../features/sprintboard/models/types';

export interface AddUpdateModalProps {
  open: boolean;
  onClose: () => void;
  taskId: string;
  taskTitle: string;
  /** Posts the update. */
  onAdd: (draft: TaskUpdateDraft) => Promise<TaskUpdate>;
}

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'on_track', label: 'On track' },
  { value: 'at_risk', label: 'At risk' },
  { value: 'blocked', label: 'Blocked' },
];

/**
 * "+ Add Update" (§15) — a compact standup update on a task: what you Completed,
 * what's Next, and (optionally) a Blocker. Writing one TaskUpdate powers both
 * the task's Updates section and the Daily Scrum roll-up.
 */
export function AddUpdateModal({
  open,
  onClose,
  taskId,
  taskTitle,
  onAdd,
}: AddUpdateModalProps) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<TaskUpdateDraft>(blankTaskUpdateDraft(taskId));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft(blankTaskUpdateDraft(taskId));
  }, [open, taskId]);

  const set = <K extends keyof TaskUpdateDraft>(key: K, value: TaskUpdateDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const submit = async () => {
    const problems = validateTaskUpdateDraft(draft);
    if (problems.length > 0) {
      toast({ tone: 'error', title: 'Cannot post update', description: problems[0] });
      return;
    }
    setSaving(true);
    try {
      // Keep status coherent with a blocker note.
      const blocker = draft.blocker?.trim() || undefined;
      const status: UpdateStatus = blocker ? 'blocked' : draft.status ?? 'on_track';
      await onAdd({ ...draft, blocker, status });
      toast({ tone: 'success', title: 'Update posted', description: taskTitle });
      onClose();
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Couldn’t post update',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? () => undefined : onClose}
      size="md"
      title={
        <span className="flex items-center gap-2">
          <MessageSquarePlus className="h-4 w-4 text-accent" aria-hidden="true" />
          Add update ·{' '}
          <span className="font-normal text-zinc-400">{taskTitle}</span>
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Post update
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Textarea
          label="Completed"
          rows={2}
          value={draft.completed}
          placeholder="What you got done since the last update."
          onChange={(e) => set('completed', e.target.value)}
        />
        <Textarea
          label="Next"
          rows={2}
          value={draft.next}
          placeholder="What you're picking up next."
          onChange={(e) => set('next', e.target.value)}
        />
        <Textarea
          label="Blocker (optional)"
          rows={2}
          value={draft.blocker ?? ''}
          placeholder="Anything in your way?"
          onChange={(e) => set('blocker', e.target.value)}
        />
        <Select
          label="Status"
          value={draft.status ?? 'on_track'}
          options={STATUS_OPTIONS}
          onChange={(e) => set('status', e.target.value as UpdateStatus)}
        />
      </div>
    </Modal>
  );
}

export default AddUpdateModal;
